import React, { useState, useEffect } from 'react';
import { collection, getDocs, updateDoc, doc, setDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { FaBed, FaRupeeSign, FaEdit, FaSave, FaTimes, FaToggleOn, FaToggleOff } from 'react-icons/fa';

interface RoomType {
    id: string;
    name: string;
    description: string;
    originalPrice: number;
    discountedPrice: number;
    discountPercentage: number;
    features: string[];
    isActive: boolean;
}

const ManageServices: React.FC = () => {
    const [roomTypes, setRoomTypes] = useState<RoomType[]>([]);
    const [editingRoom, setEditingRoom] = useState<string | null>(null);
    const [tempValues, setTempValues] = useState<{[key: string]: any}>({});

    // Initialize default room types if none exist
    const initializeDefaultRooms = async () => {
        const defaultRooms = [
            {
                id: 'suite-room',
                name: 'Suite Room',
                description: 'Luxurious suite with premium amenities and spacious layout',
                originalPrice: 6000,
                discountedPrice: 4800,
                discountPercentage: 20,
                features: ['King Size Bed', 'Private Balcony', 'Premium AC', 'Mini Fridge'],
                isActive: true
            },
            {
                id: 'single-room',
                name: 'Single Room',
                description: 'Comfortable single occupancy room with all essential amenities',
                originalPrice: 2500,
                discountedPrice: 2000,
                discountPercentage: 20,
                features: ['Queen Size Bed', 'AC', 'WiFi', 'Hot Water'],
                isActive: true
            }
        ];

        for (const room of defaultRooms) {
            await setDoc(doc(db, 'roomTypes', room.id), room);
        }
        
        setRoomTypes(defaultRooms);
    };

    useEffect(() => {
        const fetchRoomTypes = async () => {
            try {
                const querySnapshot = await getDocs(collection(db, 'roomTypes'));
                if (querySnapshot.empty) {
                    await initializeDefaultRooms();
                } else {
                    const roomTypesData = querySnapshot.docs.map(doc => ({
                        id: doc.id,
                        ...doc.data()
                    })) as RoomType[];
                    setRoomTypes(roomTypesData);
                }
            } catch (error) {
                console.error('Error fetching room types:', error);
                await initializeDefaultRooms();
            }
        };

        fetchRoomTypes();
    }, []);

    const calculateDiscountedPrice = (originalPrice: number, discountPercentage: number) => {
        return Math.round(originalPrice * (1 - discountPercentage / 100));
    };

    const startEdit = (room: RoomType) => {
        setEditingRoom(room.id);
        setTempValues({
            originalPrice: room.originalPrice,
            discountPercentage: room.discountPercentage,
            description: room.description
        });
    };

    const cancelEdit = () => {
        setEditingRoom(null);
        setTempValues({});
    };

    const saveEdit = async (roomId: string) => {
        try {
            const discountedPrice = calculateDiscountedPrice(
                tempValues.originalPrice, 
                tempValues.discountPercentage
            );
            
            const updatedData = {
                ...tempValues,
                discountedPrice
            };

            await updateDoc(doc(db, 'roomTypes', roomId), updatedData);
            setRoomTypes(roomTypes.map(room => 
                room.id === roomId ? { ...room, ...updatedData } : room
            ));
            setEditingRoom(null);
            setTempValues({});
        } catch (error) {
            console.error('Error updating room:', error);
            alert('Failed to update room. Please try again.');
        }
    };

    const toggleStatus = async (roomId: string) => {
        const room = roomTypes.find(r => r.id === roomId);
        if (room) {
            try {
                const newStatus = !room.isActive;
                await updateDoc(doc(db, 'roomTypes', roomId), { isActive: newStatus });
                setRoomTypes(roomTypes.map(r => 
                    r.id === roomId ? { ...r, isActive: newStatus } : r
                ));
            } catch (error) {
                console.error('Error toggling status:', error);
                alert('Failed to update status. Please try again.');
            }
        }
    };

    return (
        <div className="p-4 bg-white rounded-lg shadow-lg">
            {/* Simple Header */}
            <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-800 mb-2">Room Pricing</h2>
                <p className="text-gray-600 text-sm">Tap edit to change prices</p>
            </div>

            {/* Simple Room List */}
            <div className="space-y-4">
                {roomTypes.map((room) => (
                    <div key={room.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                        {/* Room Header - Always Visible */}
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center space-x-3">
                                <FaBed className="text-primary text-lg" />
                                <div>
                                    <h3 className="font-semibold text-gray-800">{room.name}</h3>
                                    <div className="flex items-center space-x-2">
                                        <span className={`text-xs px-2 py-1 rounded-full ${
                                            room.isActive 
                                                ? 'bg-green-100 text-green-700' 
                                                : 'bg-red-100 text-red-700'
                                        }`}>
                                            {room.isActive ? 'Active' : 'Inactive'}
                                        </span>
                                        <span className="text-xs text-gray-500">{room.discountPercentage}% OFF</span>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="flex items-center space-x-2">
                                <button
                                    onClick={() => toggleStatus(room.id)}
                                    className={`p-2 rounded ${
                                        room.isActive 
                                            ? 'text-green-600 hover:bg-green-100' 
                                            : 'text-red-600 hover:bg-red-100'
                                    }`}
                                >
                                    {room.isActive ? <FaToggleOn /> : <FaToggleOff />}
                                </button>
                                
                                {editingRoom === room.id ? (
                                    <>
                                        <button
                                            onClick={() => saveEdit(room.id)}
                                            className="p-2 text-green-600 hover:bg-green-100 rounded"
                                        >
                                            <FaSave />
                                        </button>
                                        <button
                                            onClick={cancelEdit}
                                            className="p-2 text-gray-600 hover:bg-gray-100 rounded"
                                        >
                                            <FaTimes />
                                        </button>
                                    </>
                                ) : (
                                    <button
                                        onClick={() => startEdit(room)}
                                        className="p-2 text-blue-600 hover:bg-blue-100 rounded"
                                    >
                                        <FaEdit />
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Pricing Display - Always Visible */}
                        <div className="grid grid-cols-3 gap-4 mb-3">
                            <div className="text-center">
                                <p className="text-xs text-gray-500">Original</p>
                                <p className="font-bold text-gray-400 line-through">₹{room.originalPrice.toLocaleString()}</p>
                            </div>
                            <div className="text-center">
                                <p className="text-xs text-gray-500">Final Price</p>
                                <p className="text-lg font-bold text-primary">₹{room.discountedPrice.toLocaleString()}</p>
                            </div>
                            <div className="text-center">
                                <p className="text-xs text-gray-500">You Save</p>
                                <p className="font-bold text-green-600">₹{(room.originalPrice - room.discountedPrice).toLocaleString()}</p>
                            </div>
                        </div>

                        {/* Edit Form - Only When Editing */}
                        {editingRoom === room.id && (
                            <div className="bg-white rounded-lg p-4 border-t border-gray-200 space-y-3">
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-1">
                                            Original Price
                                        </label>
                                        <input
                                            type="number"
                                            value={tempValues.originalPrice}
                                            onChange={(e) => setTempValues({
                                                ...tempValues,
                                                originalPrice: parseInt(e.target.value) || 0
                                            })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-primary focus:border-primary text-sm"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-1">
                                            Discount %
                                        </label>
                                        <input
                                            type="number"
                                            value={tempValues.discountPercentage}
                                            onChange={(e) => setTempValues({
                                                ...tempValues,
                                                discountPercentage: parseInt(e.target.value) || 0
                                            })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-primary focus:border-primary text-sm"
                                        />
                                    </div>
                                </div>
                                
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Description
                                    </label>
                                    <textarea
                                        value={tempValues.description}
                                        onChange={(e) => setTempValues({
                                            ...tempValues,
                                            description: e.target.value
                                        })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-primary focus:border-primary text-sm"
                                        rows={2}
                                    />
                                </div>

                                {/* Live Preview */}
                                <div className="bg-gray-50 rounded p-3">
                                    <p className="text-xs text-gray-600 mb-1">Preview:</p>
                                    <p className="text-sm">
                                        Final Price: <span className="font-bold text-primary">
                                            ₹{calculateDiscountedPrice(tempValues.originalPrice, tempValues.discountPercentage).toLocaleString()}
                                        </span>
                                    </p>
                                    <p className="text-xs text-green-600">
                                        Save: ₹{(tempValues.originalPrice - calculateDiscountedPrice(tempValues.originalPrice, tempValues.discountPercentage)).toLocaleString()}
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Description - Always Visible */}
                        {editingRoom !== room.id && (
                            <p className="text-sm text-gray-600">{room.description}</p>
                        )}
                    </div>
                ))}
            </div>

            {/* Simple Stats */}
            <div className="mt-6 grid grid-cols-2 gap-4">
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                    <p className="text-sm text-blue-600">Total Rooms</p>
                    <p className="text-xl font-bold text-blue-700">{roomTypes.length}</p>
                </div>
                <div className="bg-green-50 rounded-lg p-3 text-center">
                    <p className="text-sm text-green-600">Active Rooms</p>
                    <p className="text-xl font-bold text-green-700">{roomTypes.filter(r => r.isActive).length}</p>
                </div>
            </div>
        </div>
    );
};

export default ManageServices;